import React, { useState, useEffect, useRef } from 'react';
import { config } from '../../config/config';

const AuthLayout = ({ children }) => {
  const [systemStatus, setSystemStatus] = useState('checking');
  const [lastChecked, setLastChecked] = useState(null);
  const canvasRef = useRef(null);

  // API Health Check
  useEffect(() => {
    const checkApiStatus = async () => {
      try {
        const now = new Date();
        setLastChecked(now);
        
        const response = await fetch(`${config.apiUrl}/api/users/status`, {
          method: 'GET',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': 'Bearer test-token'
          },
          signal: AbortSignal.timeout(5000)
        });
        
        if (response) {
          setSystemStatus('online');
        } else {
          setSystemStatus('error');
        }
      } catch (error) {
        // Silent error for UI polish, just update status
        if (error.name === 'TimeoutError') {
          setSystemStatus('timeout');
        } else if (error.name === 'AbortError') {
          setSystemStatus('aborted');
        } else if (error.message && error.message.includes('Failed to fetch')) {
          setSystemStatus('offline');
        } else {
          setSystemStatus('error');
        }
      }
    };

    checkApiStatus();
    const intervalId = setInterval(checkApiStatus, 30000);
    return () => clearInterval(intervalId);
  }, []);

  // Professional Constellation Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width, height;
    let animationFrameId;
    let particles = [];

    // Configuration
    const particleCount = 60; // Sparse for elegance
    const connectionDistance = 180;
    const mouseDistance = 250;

    let mouse = { x: null, y: null };

    const resize = () => {
      width = canvas.width = canvas.parentElement.offsetWidth;
      height = canvas.height = canvas.parentElement.offsetHeight;
    };

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.3; // Very slow movement
        this.vy = (Math.random() - 0.5) * 0.3;
        this.size = Math.random() * 1.5 + 0.5; // Small, subtle dots
        this.baseColor = 'rgba(255, 255, 255,'; // White base
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Gentle bounce off walls
        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;

        // Mouse interaction
        if (mouse.x != null) {
          let dx = mouse.x - this.x;
          let dy = mouse.y - this.y;
          let distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < mouseDistance) {
            const forceDirectionX = dx / distance;
            const forceDirectionY = dy / distance;
            const force = (mouseDistance - distance) / mouseDistance;
            const directionX = forceDirectionX * force * 0.6;
            const directionY = forceDirectionY * force * 0.6;
            this.vx -= directionX * 0.05;
            this.vy -= directionY * 0.05;
          }
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `${this.baseColor} 0.4)`;
        ctx.fill();
      }
    }

    const init = () => {
      resize();
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            const opacity = 1 - (distance / connectionDistance);
            ctx.beginPath();
            ctx.strokeStyle = `rgba(255, 255, 255, ${opacity * 0.15})`; // Very subtle lines
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    canvas.addEventListener('mousemove', (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    });
    canvas.addEventListener('mouseleave', () => {
      mouse.x = null;
      mouse.y = null;
    });

    init();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const getStatusColor = () => {
    switch (systemStatus) {
      case 'online': return 'bg-emerald-400';
      case 'checking': return 'bg-blue-400';
      case 'offline': return 'bg-red-500';
      default: return 'bg-amber-400';
    }
  };

  const getStatusText = () => {
    switch (systemStatus) {
      case 'online': return 'System Operational';
      case 'checking': return 'Connecting...';
      case 'offline': return 'Offline';
      default: return 'Connection Issue';
    }
  };

  // Security Tips Slider logic
  const [currentTip, setCurrentTip] = useState(0);
  const tips = [
    {
      title: "Internal System Access",
      description: "This platform contains proprietary data. Access is restricted to authorized personnel only and all activity is logged.",
      icon: (
        <svg className="w-12 h-12 text-blue-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: "Data Confidentiality",
      description: "Client information and project details must be handled according to company privacy policies. Do not export data without approval.",
      icon: (
        <svg className="w-12 h-12 text-blue-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )
    },
    {
      title: "Session Security",
      description: "For security reasons, your session will expire after a period of inactivity. Please save your work regularly.",
      icon: (
        <svg className="w-12 h-12 text-blue-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "System Support",
      description: "If you encounter any technical issues or need access permissions changed, please contact your system administrator.",
      icon: (
        <svg className="w-12 h-12 text-blue-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    }
  ];

  useEffect(() => {
    const tipInterval = setInterval(() => {
      setCurrentTip((prev) => (prev + 1) % tips.length);
    }, 5000);
    return () => clearInterval(tipInterval);
  }, [tips.length]);

  return (
    <div className="flex min-h-screen bg-white font-sans text-slate-900">
      {/* Left Side - Professional Animation */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-[#0B1120]">
        {/* Deep Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#172554] opacity-90"></div>
        
        {/* Animated Mesh Gradient blobs for depth */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 -left-40 w-[500px] h-[500px] bg-blue-600/20 rounded-full mix-blend-screen filter blur-[100px] animate-pulse duration-10000"></div>
          <div className="absolute bottom-0 -right-40 w-[600px] h-[600px] bg-indigo-600/10 rounded-full mix-blend-screen filter blur-[120px] animate-pulse duration-[15000ms]"></div>
        </div>

        {/* Interactive Canvas Overlay */}
        <canvas ref={canvasRef} className="absolute inset-0 z-10" />

        {/* Content Overlay */}
        <div className="relative z-20 w-full h-full flex flex-col justify-between p-16">
          {/* Top Area */}
          <div>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/10">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <span className="text-white/90 font-semibold tracking-wide text-sm">SECURITY FIRST</span>
            </div>
          </div>

          {/* Central Security Tips Slider */}
          <div className="flex flex-col justify-center max-w-xl relative h-64">
            {tips.map((tip, index) => (
              <div 
                key={index}
                className={`absolute transition-all duration-700 ease-in-out transform ${
                  index === currentTip 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-4 pointer-events-none'
                }`}
              >
                {tip.icon}
                <h2 className="text-3xl font-bold text-white mb-4 tracking-tight">
                  {tip.title}
                </h2>
                <p className="text-slate-400 text-lg leading-relaxed">
                  {tip.description}
                </p>
              </div>
            ))}
            
            {/* Slider Indicators */}
            <div className="absolute -bottom-12 flex space-x-2">
              {tips.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTip(index)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    index === currentTip ? 'w-8 bg-blue-400' : 'w-2 bg-slate-600 hover:bg-slate-500'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Bottom Status Bar */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-3 bg-white/5 backdrop-blur-md px-4 py-2 rounded-full border border-white/5 transition-all hover:bg-white/10">
              <span className={`relative flex h-2.5 w-2.5`}>
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${getStatusColor()}`}></span>
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${getStatusColor()}`}></span>
              </span>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-slate-200">{getStatusText()}</span>
              </div>
            </div>
            {lastChecked && (
              <div className="text-[11px] text-slate-500 font-mono">
                SYNC: {lastChecked.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Form Area */}
      <div className="w-full lg:w-[45%] flex flex-col items-center justify-center bg-white relative p-8">
        <div className="w-full max-w-md relative z-10">
          {children}
        </div>

        <div className="absolute bottom-6 text-center text-slate-400 text-xs">
          &copy; {new Date().getFullYear()} Topsail roofing. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
